import { ApolloClient, gql, useApolloClient } from '@apollo/client';
import { hasOneOfScopes } from '@sixfold/session-interface';
import { isNil } from '@sixfold/typed-primitives';
import { useEffect, useState } from 'react';

import { Routes } from '../../routes';
import { BackofficeRole, ViewerBackofficeRoleQuery } from '../graphql';
import { AuthService } from '../login';
import { BackofficeSessionState, getBackofficeSessionState, sessionStateForQueryError } from './session_state';

const backofficeRoleQuery = gql`
  query ViewerBackofficeRole {
    viewer {
      backoffice_role
      backoffice_scopes
      backoffice_access_enabled
    }
  }
`;

const viewerQuery = gql`
  query Viewer {
    viewer {
      user_id
      first_name
      last_name
      email
      backoffice_role
      backoffice_scopes
    }
  }
`;

type CurrentUser = {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  backoffice_role: string | null;
  backoffice_scopes: string[] | null;
};

export function useCurrentUser() {
  const client = useApolloClient();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(() => {
    try {
      const data = client.readQuery({ query: viewerQuery });
      return data?.viewer || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    client
      .query({ query: viewerQuery, fetchPolicy: 'cache-first' })
      .then((result) => {
        setCurrentUser(result?.data?.viewer || null);
      })
      .catch(console.error);
  }, [client]);

  return currentUser;
}

export function useIsAdmin() {
  const client = useApolloClient();
  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    try {
      const data = client.readQuery<ViewerBackofficeRoleQuery>({ query: backofficeRoleQuery });
      return data?.viewer?.backoffice_role === BackofficeRole.admin;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    client
      .query<ViewerBackofficeRoleQuery>({ query: backofficeRoleQuery, fetchPolicy: 'cache-first' })
      .then((result) => {
        setIsAdmin(result?.data?.viewer?.backoffice_role === BackofficeRole.admin);
      })
      .catch(console.error);
  }, [client]);

  return isAdmin;
}

export function useHasScopes(requiredScopes: string[]) {
  const client = useApolloClient();
  const [hasScopes, setHasScopes] = useState<boolean>(() => {
    try {
      const data = client.readQuery<ViewerBackofficeRoleQuery>({ query: backofficeRoleQuery });
      return hasOneOfScopes(data?.viewer?.backoffice_scopes ?? [], requiredScopes);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    client
      .query<ViewerBackofficeRoleQuery>({ query: backofficeRoleQuery, fetchPolicy: 'cache-first' })
      .then((result) => {
        setHasScopes(hasOneOfScopes(result.data?.viewer?.backoffice_scopes ?? [], requiredScopes));
      })
      .catch(console.error);
  }, [client, requiredScopes]);

  return hasScopes;
}

function readBackofficeSessionState(client: ApolloClient<object>): BackofficeSessionState {
  try {
    const data = client.readQuery<ViewerBackofficeRoleQuery>({ query: backofficeRoleQuery });
    return getBackofficeSessionState(data?.viewer);
  } catch {
    return 'unauthenticated';
  }
}

export function useIsAuthenticated(client: ApolloClient<object>) {
  const [isLoading, setIsLoading] = useState(true);
  const [sessionState, setSessionState] = useState<BackofficeSessionState>(() => readBackofficeSessionState(client));

  useEffect(() => {
    let active = true;

    client
      .query<ViewerBackofficeRoleQuery>({ query: backofficeRoleQuery, fetchPolicy: 'cache-first' })
      .then((result) => {
        if (!active) {
          return;
        }
        setSessionState(getBackofficeSessionState(result.data?.viewer));
      })
      .catch((error: unknown) => {
        console.error(error);
        if (active) {
          setSessionState(sessionStateForQueryError(error));
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [client]);

  return {
    isAuthenticated: sessionState === 'authenticated',
    isLoading,
    shouldClearSession: sessionState === 'clear-session',
  };
}

export const getAuthService = () => {
  const authService: AuthService = {
    async authenticate(serviceIdToken: string) {
      const response = await fetch('/authenticate', {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ token: serviceIdToken }),
      });

      const data = await response.json();

      if (data.error) {
        return await Promise.reject(data);
      }

      // TODO: We shouldn't need this, the redirect should happen in some other way
      const redirectPath = sanitizeRedirectPath(new URLSearchParams(window.location.search));
      window.location.href = redirectPath ?? '/';
      return await Promise.resolve({ isLoggedIn: true });
    },
  };

  return authService;
};

function sanitizeRedirectPath(params: URLSearchParams) {
  const path = params.get('redirect');
  if (isNil(path) || path === '') {
    return;
  }

  try {
    const redirectedUrl = new URL(path, window.location.href);
    if (redirectedUrl.origin === window.location.origin) {
      return redirectedUrl.href;
    }

    return;
  } catch {
    return;
  }
}

export function getRedirectSearchParam() {
  const pathname = window.location.pathname;
  if (
    pathname === '/' ||
    pathname.startsWith(Routes.Login.routerPath) ||
    pathname.startsWith(Routes.Logout.routerPath)
  ) {
    return '';
  }
  const currentPath = pathname + window.location.search;
  return `?redirect=${encodeURIComponent(currentPath)}`;
}
